// src/pages/Depreciation.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

export default function Depreciation() {
  const { projectId } = useParams();

  // ─── State ────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inputs, setInputs] = useState(null);
  const [rows, setRows] = useState([]);
  const [projectYears, setProjectYears] = useState([]);
  const [termLoanInput, setTermLoanInput] = useState({ startMonth: "" });

  // ─── Fetch from API ───────────────────────────────────────
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        setInputs(data);
        setRows(
          Array.isArray(data.depreciationData) && data.depreciationData.length
            ? data.depreciationData
            : [
                "Land & Site Development (On Lease)",
                "Civil Construction (On Lease)",
                "Plant & Machinery",
                "Furniture and Fixture",
                "Miscellaneous Fixed Assets",
                "Computer and Software",
                "Preliminary Expenses",
              ].map((p, i) => ({
                slno: i + 1,
                particular: p,
                taxPercent: 0,
              }))
        );
        setProjectYears(data.projectYears || []);
        setTermLoanInput(data.termLoanInput || { startMonth: "" });
      })
      .catch(() => setError("Failed to load depreciation data."))
      .finally(() => setLoading(false));
  }, [projectId]);

  // ─── Parse “mm-yyyy” → { month, year } ──────────────────────
  const prod = useMemo(() => {
    const [m, y] = (termLoanInput.startMonth || "").split("-").map(Number);
    return m && y ? { month: m, year: y } : null;
  }, [termLoanInput.startMonth]);

  // ─── Months active in FY “YYYY-YY” ────────────────────────
  const getMonthsActive = useCallback(
    (fyLabel) => {
      if (!prod) return 12;
      const fyStart = parseInt(fyLabel.slice(0, 4), 10);
      if (isNaN(fyStart)) return 12;
      const fyEnd = fyStart + 1;
      const raw = (fyEnd - prod.year) * 12 + (3 - prod.month + 1);
      return Math.max(0, Math.min(raw, 12));
    },
    [prod]
  );

  // ─── Full-year declining‐balance dep’n for yearIndex ──────
  const calcFullYear = (cost, rate, yearIndex) => {
    if (yearIndex === 0) return 0;
    let remaining = cost,
      dep = 0;
    for (let i = 1; i <= yearIndex; i++) {
      dep = (remaining * rate) / 100;
      remaining -= dep;
    }
    return dep;
  };

  // ─── Cost lookup from API inputs ───────────────────────────
  const getCostByParticular = (p) => {
    if (!inputs) return 0;

    const {
      costProjectInputs = {},
      plantMachineryData = [],
      plantContingencies,
      plantGstPercent,
      furnitureAndFixtures = [],
      otherFixedAssets = [],
      computers = [],
      preliminaryExpenses = [],
    } = inputs;

    const keyMap = {
      "Land & Site Development (On Lease)": "landDevelopment",
      "Civil Construction (On Lease)": "civilConstruction",
    };
    if (keyMap[p] && costProjectInputs[keyMap[p]] != null) {
      return parseFloat(costProjectInputs[keyMap[p]]) || 0;
    }

    switch (p) {
      case "Plant & Machinery": {
        const subtotal = plantMachineryData.reduce(
          (s, r) => s + (parseFloat(r.amount) || 0),
          0
        );
        const cont = (subtotal * (parseFloat(plantContingencies) || 0)) / 100;
        const tax =
          ((subtotal + cont) * (parseFloat(plantGstPercent) || 0)) / 100;
        return subtotal + cont + tax;
      }
      case "Furniture and Fixture":
        return furnitureAndFixtures.reduce(
          (s, r) => s + (parseFloat(r.amount) || 0),
          0
        );
      case "Miscellaneous Fixed Assets":
        return otherFixedAssets.reduce(
          (s, r) => s + (parseFloat(r.amount) || 0),
          0
        );
      case "Computer and Software":
        return computers.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
      case "Preliminary Expenses":
        return preliminaryExpenses.reduce(
          (s, r) => s + (parseFloat(r.amount) || 0),
          0
        );
      default:
        return 0;
    }
  };

  // ─── Build table data & totals ────────────────────────────
  const tableData = useMemo(() => {
    if (!inputs) return null;

    const withDep = rows.map((row) => {
      const cost = getCostByParticular(row.particular);
      const perYear = projectYears.map((fy, yi) => {
        const full = calcFullYear(cost, row.taxPercent, yi);
        const months = getMonthsActive(fy);
        return +(full * (months / 12)).toFixed(2);
      });
      return { ...row, cost, perYear };
    });

    const totals = projectYears.map((_, yi) =>
      withDep.reduce((s, r) => s + r.perYear[yi], 0)
    );
    const costTotal = withDep.reduce((s, r) => s + r.cost, 0);

    return { withDep, totals, costTotal };
  }, [rows, inputs, projectYears, getMonthsActive]);

  const fmt = (n) =>
    typeof n === "number"
      ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : "";

  if (loading)
    return (
      <div className="flex h-64 items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-gray-500"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
      </div>
    );
  if (error)
    return <div className="text-red-600 text-center py-8">{error}</div>;
  if (!tableData) return null;

  return (
    <div className="bg-white">
      <div className="overflow-x-auto mb-8 shadow-lg rounded-lg">
        <table className="min-w-full table-auto border-collapse">
          <thead className="bg-gray-100">
            <tr className="h-8">
              <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                Particular
              </th>
              <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                Cost
              </th>
              <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                Rate %
              </th>
              {projectYears.map((yr, i) => (
                <th
                  key={i}
                  className="px-2 py-1 border border-gray-200 text-center font-medium text-sm"
                >
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.withDep.map((r, i) => (
              <tr key={i} className={i % 2 ? "even:bg-gray-50" : ""}>
                <td className="px-2 py-1 border border-gray-200">
                  {r.particular}
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  {fmt(r.cost)}
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  {fmt(r.taxPercent)}
                </td>
                {r.perYear.map((val, yi) => (
                  <td
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-right"
                  >
                    {fmt(val)}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-gray-200 font-bold">
              <td className="px-2 py-1 border border-gray-200 text-right">
                Total
              </td>
              <td className="px-2 py-1 border border-gray-200 text-right">
                {fmt(tableData.costTotal)}
              </td>
              <td className="px-2 py-1 border border-gray-200" />
              {tableData.totals.map((t, yi) => (
                <td
                  key={yi}
                  className="px-2 py-1 border border-gray-200 text-right"
                >
                  {fmt(t)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
